import { useMemo } from 'react';
import { Users } from 'lucide-react';
import type { VerbatimRespondent } from '../../data/verbatimData';
import { InfoButton } from '../common/Tooltip';

function mode(arr: string[]): string {
  const counts: Record<string, number> = {};
  for (const v of arr) if (v) counts[v] = (counts[v] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] || 'N/A';
}

function pct(count: number, total: number): string {
  return total > 0 ? `${Math.round(count / total * 100)}%` : '0%';
}

function topRankedFeature(list: VerbatimRespondent[]): string {
  const sums: Record<string, number> = {};
  const counts: Record<string, number> = {};
  for (const r of list) {
    for (const [f, rank] of Object.entries(r.featureRanking)) {
      if (rank > 0) {
        sums[f] = (sums[f] || 0) + rank;
        counts[f] = (counts[f] || 0) + 1;
      }
    }
  }
  let best = '';
  let bestAvg = 99;
  for (const [f, sum] of Object.entries(sums)) {
    const avg = sum / (counts[f] || 1);
    if (avg < bestAvg) { bestAvg = avg; best = f; }
  }
  return best ? `${best.split(' ')[0]} (#${Math.round(bestAvg)} avg)` : 'N/A';
}

interface Props {
  respondents: VerbatimRespondent[];
  isFiltered: boolean;
}

export function GroupProfileCard({ respondents, isFiltered }: Props) {
  const stats = useMemo(() => {
    const n = respondents.length;
    if (n === 0) return null;

    const femaleCount = respondents.filter(r => r.gender === 'Female').length;
    const ages = respondents.map(r => r.age).filter(a => a > 0);
    const avgAge = ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
    const topIncome = mode(respondents.map(r => r.income));

    const avgNone = +(respondents.reduce((s, r) => s + r.noneCount, 0) / n).toFixed(1);
    const chosenPrices = respondents.filter(r => r.avgChosenPrice !== null);
    const avgPrice = chosenPrices.length > 0
      ? Math.round(chosenPrices.reduce((s, r) => s + r.avgChosenPrice!, 0) / chosenPrices.length)
      : null;
    const objectionCount = respondents.filter(r => r.priceObjection.length > 0).length;

    const topGoal = mode(respondents.map(r => r.primaryGoal));
    const topFeature = topRankedFeature(respondents);
    const nothingSpend = respondents.filter(r => r.monthlySpend.toLowerCase().includes('nothing')).length;

    return {
      n: n,
      femalePct: pct(femaleCount, n),
      avgAge,
      topIncome: topIncome.replace(/\$/g, '$').split(' - ')[0],
      avgNone,
      avgPrice,
      objectionPct: pct(objectionCount, n),
      topGoal: topGoal.length > 40 ? topGoal.slice(0, 37) + '...' : topGoal,
      topFeature,
      nothingSpendPct: pct(nothingSpend, n),
    };
  }, [respondents]);

  if (!isFiltered || !stats) return null;

  return (
    <div className="card mb-4 border-l-4 border-l-chart-teal">
      <div className="flex items-center gap-2 mb-3">
        <Users size={14} className="text-chart-teal" />
        <span className="text-sm font-semibold text-primary">
          Group Profile — {stats.n.toLocaleString()} respondents
        </span>
        <InfoButton helpKey="groupProfile" size={12} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">Demographics</div>
          <div className="space-y-1.5">
            <ProfileStat label="Gender" value={<>{stats.femalePct} Female</>} />
            <ProfileStat label="Avg Age" value={String(stats.avgAge)} />
            <ProfileStat label="Top Income" value={stats.topIncome} />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">Conjoint Behavior</div>
          <div className="space-y-1.5">
            <ProfileStat label="None Rate" value={<>{stats.avgNone}<span className="text-muted">/15</span></>} />
            {stats.avgPrice !== null && <ProfileStat label="Avg Price" value={`$${stats.avgPrice.toLocaleString()}`} />}
            <ProfileStat label="Price Objection" value={stats.objectionPct} />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-2">Stated Priorities</div>
          <div className="space-y-1.5">
            <ProfileStat label="Top Goal" value={stats.topGoal} mono={false} />
            <ProfileStat label="Top Feature" value={stats.topFeature} mono={false} />
            <ProfileStat label="$0 Spend" value={stats.nothingSpendPct} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileStat({ label, value, mono = true }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-micro text-muted shrink-0">{label}</span>
      <span className={`text-xs text-primary font-medium text-right ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</span>
    </div>
  );
}
